/**
 * Pure helpers for the customer survey screens (tracker item 69): paths,
 * labels, rating text, the rate and average formats, and error messages.
 */
import * as types from '../../api/types';
import { getErrorCode, getErrorMessage } from '../../utils/apiError';

export const SURVEYS_PATH = '/surveys';
export const SURVEY_SETTINGS_PATH = '/settings/surveys';
export const SATISFACTION_REPORT_PATH = '/reports/customer-satisfaction';
/** The page that edits the wording of the survey email (ADMIN). */
export const SURVEY_TEMPLATE_PATH = '/settings/notifications/messages/SURVEY_REQUEST';

export const RATING_LABELS: Record<number, string> = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very good', 5: 'Excellent' };
export const RATINGS = [1, 2, 3, 4, 5] as const;

export function ratingLabel(rating: number, labels: Record<number, string> = RATING_LABELS): string {
  return labels[rating] ?? String(rating);
}

/** "4 out of 5", for screen readers and plain text. */
export function ratingText(rating: number, max = 5): string {
  return `${rating} out of ${max}`;
}

/** A rating from a query string value ("?rating=4"), or null when it is not 1 to 5. */
export function ratingParam(value: string | null | undefined, min = 1, max = 5): number | null {
  if (!value || !/^\d$/.test(value)) return null;
  const n = Number(value);
  return n >= min && n <= max ? n : null;
}

/** "4.25" style average to one or two decimals; an em dash without data. */
export function averageText(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return (Math.round(value * 100) / 100).toFixed(2).replace(/0$/, '');
}

/** 0.8 -> "80%", 0.667 -> "66.7%"; an em dash without data. */
export function rateText(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const percent = Math.round(value * 1000) / 10;
  return `${Number.isInteger(percent) ? percent.toFixed(0) : percent.toFixed(1)}%`;
}

export const REQUEST_STATUS_LABELS: Record<types.SurveyRequestStatus, string> = {
  PENDING: 'Scheduled',
  SENT: 'Sent',
  ANSWERED: 'Answered',
  EXPIRED: 'Expired',
  CANCELLED: 'Not sent',
};

export const REQUEST_STATUS_CLASSES: Record<types.SurveyRequestStatus, string> = {
  PENDING: 'bg-blue-50 text-blue-800',
  SENT: 'bg-blue-50 text-blue-800',
  ANSWERED: 'bg-green-50 text-green-800',
  EXPIRED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-gray-100 text-gray-700',
};

/** One line describing a job's survey request for the job page. */
export function requestSummaryText(request: types.SurveyRequestSummary): string {
  const date = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');
  switch (request.status) {
    case 'PENDING':
      return `Scheduled to go out ${date(request.scheduledFor)}.`;
    case 'SENT':
      return `Sent ${date(request.sentAt)}${request.sendCount > 1 ? ` (${request.sendCount} times)` : ''}; the link works until ${date(request.expiresAt)}.`;
    case 'ANSWERED':
      return `Answered ${date(request.answeredAt)}.`;
    case 'EXPIRED':
      return `Sent ${date(request.sentAt)}; the link expired ${date(request.expiresAt)}.`;
    default:
      return `Not sent${request.cancelReasonText ? `: ${request.cancelReasonText}` : ''}.`;
  }
}

/** The text for a failed survey call; the backend's message wins. */
export function surveyErrorText(cause: unknown, fallback: string): string {
  return getErrorMessage(cause, fallback);
}

/** What the public page should show for a failed answer. */
export function publicAnswerOutcome(cause: unknown): 'answered' | 'expired' | 'unavailable' | 'error' {
  const code = getErrorCode(cause);
  if (code === types.SURVEY_ERROR_CODES.alreadyAnswered) return 'answered';
  if (code === types.SURVEY_ERROR_CODES.expired) return 'expired';
  const status = (cause as { response?: { status?: number } } | null)?.response?.status;
  if (status === 404) return 'unavailable';
  return 'error';
}

/** Weekday-less short date for a YYYY-MM-DD or ISO value. */
export function surveyDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}
